import { CompositeFilterDescriptor, DataSourceRequestState, State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { FilterChangeEvent, HeaderTdElement } from '@progress/kendo-react-data-tools';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridCustomCellProps, GridCustomFilterCellProps, GridDataStateChangeEvent, GridEvent, GridFilterChangeEvent, GridHandle, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { Loader } from "@progress/kendo-react-indicators";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import EmailCell from '../../components/cells/EmailCell';
import PhoneCellAndExtension from '../../components/cells/PhoneCellAndExtension';
import CenterDivPanel from '../../components/CenterDivPanel';
import { fetchApi } from '../../services/api';
import { IDNameViewModel } from '../../TypeGen/id-name-view-model';
import { ILink } from '../../types/link';
import { Title } from '../../utils/title';
import { contactTypeOptions } from './ContactTypeOptions';

export type CarrierContactViewModel = {
  CarrierContactID: number;
  FullName: string;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  PhoneNumberExtension: string;
  SendTruckAvailability: boolean;
  ContactType: string;
  ContactTypeName: string;
  EMailAddress: string;
  Active: boolean;
  CarrierName: string;
  Hash: string;
  Links: ILink[];
}

const PageSize = 50;

export const CarrierContactCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: CarrierContactViewModel = props.dataItem;
  const carrierContactProfile = dataItem.Links.find((x: ILink) => x.Name === 'CarrierContactProfile');
  return (
    <td {...props.tdProps}>
      {carrierContactProfile &&
        <Link
          to={carrierContactProfile.Link}
          style={{ color: '#007bff' }}
        >
          {dataItem.FullName || "N/A"}
        </Link>}
    </td>
  );
};

export const ContactTypeNameCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: CarrierContactViewModel = props.dataItem;
  return (
    <td {...props.tdProps}>
      {dataItem.ContactTypeName}
    </td>
  );
};

const CarrierContactGrid = () => {
  const resetDataState = {
    skip: 0,
    take: PageSize,
    sort: [{
      field: "FullName",
      dir: "asc"
    }]
  } as GridState;

  const defaultAndFilter = {
    logic: 'and', filters: [
      { field: 'Active', operator: 'eq', value: true },
    ]
  } as CompositeFilterDescriptor;

  const sessionStorageAndFilter = sessionStorage.getItem("CarrierContactGrid-andFilter");
  let andFilter: CompositeFilterDescriptor = null;
  if (sessionStorageAndFilter) {
    andFilter = JSON.parse(sessionStorageAndFilter);
  } else {
    andFilter = { ...defaultAndFilter };
  }
  const [dataState, setDataState] = useState<DataSourceRequestState>({ ...resetDataState, filter: andFilter });
  const [loading, setLoading] = useState(false);
  const [carrierContacts, setCarrierContacts] = useState<CarrierContactViewModel[]>([]);
  //const [showAddContactDialog, setShowAddContactDialog] = useState(false);
  //const buttonAnchor = useRef<ButtonHandle>(null);
  const gridRef = useRef<GridHandle>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, [])

  const fetchRecords = useCallback((append: boolean) => {
    sessionStorage.setItem("CarrierContactGrid-andFilter", JSON.stringify(dataState.filter));
    setLoading(true);
    const dataGridState = { ...dataState };
    if (!append) {
      dataGridState.take = PageSize;
      dataGridState.skip = 0;
      window.scrollTo(0, 0);
      gridRef.current?.element?.querySelector<HTMLElement>('.k-grid-content')?.scrollTo(0, 0);
    }

    //Todo - Add as a top filter
    const data = {
      CompanyActive: true
    };
    const queryStr = `${toDataSourceRequestString(dataGridState)}`;
    fetchApi(`/api/Asset/CarrierContacts?${queryStr}`, data, 'POST')
      .then(({ Data, Total }) => {
        setTotalRecords(Total);
        if (!append) {
          setCarrierContacts(Data);
        } else {
          setCarrierContacts(prevData => prevData.concat(Data));
        }
        setLoading(false);
        handleResize(null);
      }).catch(() => {
        setLoading(false);
      });
  }, [dataState]);

  const search = useDebouncedCallback(useCallback(() => {
    fetchRecords(false);
  }, [fetchRecords]), 750);

  useEffect(() => {
    search();
  }, [dataState.filter, dataState.sort, search]);

  //useEffect(() => {
  //    fetchRecords(false);
  //    // eslint-disable-next-line react-hooks/exhaustive-deps
  //}, []);

  const handleResize = (e: UIEvent) => {
    if ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement != null) {
      ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement).style.height = (document.documentElement.clientHeight - 250) + "px";
    }
  }

  const scrollHandler = (event: GridEvent) => {
    const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
    const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
      && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

    if (!isNearBottom || loading || carrierContacts.length >= totalRecords)
      return;

    dataState.skip = carrierContacts.length;
    fetchRecords(true);
  }

  const CarrierCell = (props: GridCustomCellProps) => {
    if (!props.field)
      return null;

    const dataItem: CarrierContactViewModel = props.dataItem;
    const carrierProfile = dataItem.Links.find((x: ILink) => x.Name === 'CarrierProfile');
    //props.dataItem[props.field]
    return (
      <td {...props.tdProps}>
        {carrierProfile &&
          <Link
            to={carrierProfile.Link}
            style={{ color: '#007bff' }}
          >
            {dataItem.CarrierName}
          </Link>}
      </td>
    );
  };

  const dataStateChange = (changeEvent: GridDataStateChangeEvent) => {
    setDataState(changeEvent.dataState);
  }

  const onFilterChange = (event: FilterChangeEvent) => {
    setDataState({ ...dataState, filter: event.filter });
  };

  const onGridFilterChange = (event: GridFilterChangeEvent) => {
    setDataState({ ...dataState, filter: event.filter ?? { ...defaultAndFilter } });
  };

  const onGridSortChange = (event: GridSortChangeEvent) => {
    setDataState({ ...dataState, sort: event.sort });
  };

  return (
    //<div className="container-fluid" onKeyDown={handleKey}>
    <div className="container-fluid">
      <Title string="Carrier Contact Listing" />
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}
      <Grid
        ref={gridRef}
        {...dataState}
        scrollable="scrollable"
        onDataStateChange={dataStateChange}
        data={carrierContacts}
        resizable={true}
        reorderable={true}
        sortable={true}
        onScroll={scrollHandler}
        filterable={true}
        onFilterChange={(e) => onGridFilterChange(e)}
        onSortChange={(e) => onGridSortChange(e)}
        className="mt-3"
      >
        <Column field="FullName" title="Name" cells={{ data: CarrierContactCell }} />
        <Column field="EMailAddress" title="E-Mail" cells={{ data: EmailCell }} />
        <Column field="PhoneNumber" title="Phone" cells={{ data: PhoneCellAndExtension }} />
        <Column
          field="ContactType"
          title="Contact Type"
          cells={{
            data: ContactTypeNameCell,
            filterCell: (props) => (
              <ContactTypeFilterCell
                {...props}
                data={contactTypeOptions}
              />
            )
          }}
        />
        <Column field="CarrierNumber" title="Carrier" cells={{ data: CarrierCell }} filterable={false} />
      </Grid>
      {
        totalRecords > 0 &&
        <div className="k-pager k-pager-md k-grid-pagert">
          <div style={{ marginLeft: "auto", marginRight: 0 }}>
            {formatNumber(Math.min(carrierContacts.length, totalRecords), 'n0')} of {formatNumber(totalRecords, 'n0')} items
          </div>
        </div>
      }
    </div>
  )
}

export default CarrierContactGrid;


type ContactTypeFilterCellProps = {
  data: IDNameViewModel[];
} & GridCustomFilterCellProps;

export const ContactTypeFilterCell = (props: ContactTypeFilterCellProps) => {
  const dataWithDefault = [
    { ID: -1, Name: 'Select' },
    ...props.data
  ];

  const selectedValue =
    props.value === undefined || props.value === ''
      ? -1
      : props.value;

  const onChange = (event: DropDownListChangeEvent) => {
    const selected = event.target.value;

    const isDefault = selected.ID === -1;

    props.onChange({
      value: isDefault ? '' : selected.ID,
      operator: isDefault ? '' : 'eq',
      syntheticEvent: event.syntheticEvent
    });
  };

  return (
    <HeaderTdElement {...props.thProps}>
      <span className="k-filtercell">
        <DropDownList
          data={dataWithDefault}
          value={dataWithDefault.find(x => x.ID === selectedValue)}
          onChange={onChange}
          textField="Name"
          dataItemKey="ID"
        />
      </span>
    </HeaderTdElement>
  );
};
